// =====================================================================
// apps/admin/src/main.js — Shell do app da imobiliária.
//
// Splash da marca → login → app com barra lateral e rodapé.
//
// A permissão de cada aba é decidida no BANCO (função pode_ver). O front
// só desenha o que o banco liberou. Esconder aba no front não é segurança
// — a segurança é a RLS. Mas é cortesia: a funcionária não deve nem
// enxergar a aba Financeiro.
// =====================================================================

import { sb, slugDoHost, carregarBranding, aplicarTema, meuPerfil } from '../../../packages/ui/cliente.js';
import { montarSprite, icon, ICON_CSS } from '../../../packages/ui/icons.js';
import { escapeHtml } from '../../../packages/ui/format.js';
import { iniciarTema, botaoTemaHtml, ligarTema, ativarOlhos } from '../../../packages/ui/tema.js';
import { ligarSwipe, ligarZoomFotos, ehTouch } from '../../../packages/ui/gestos.js';

import { pagInicio }       from './paginas/inicio.js';
import { pagCadastro }     from './paginas/cadastro.js';
import { pagImoveis }      from './paginas/imoveis.js';
import { pagContratos }    from './paginas/contratos.js';
import { pagFinanceiro }   from './paginas/financeiro.js';
import { pagComunicacao }  from './paginas/comunicacao.js';
import { pagNotificacoes } from './paginas/notificacoes.js';
import { pagFuncionarios } from './paginas/funcionarios.js';
import { pagUsuarios }     from './paginas/usuarios.js';
import { minhaConta, mostrarFoto } from './paginas/conta.js';

const app = document.getElementById('app');

export const estado = { perfil: null, tenant: null, permissoes: {}, modulos: {} };

const ABAS = [
  { id: 'inicio',       nome: 'Início',       icone: 'dashboard',  render: pagInicio },
  { id: 'cadastro',     nome: 'Cadastro',     icone: 'inquilino',  render: pagCadastro },
  { id: 'imoveis',      nome: 'Imóveis',      icone: 'imovel',     render: pagImoveis },
  { id: 'contratos',    nome: 'Contratos',    icone: 'contrato',   render: pagContratos },
  { id: 'financeiro',   nome: 'Financeiro',   icone: 'financeiro', render: pagFinanceiro },
  { id: 'comunicacao',  nome: 'Comunicação',  icone: 'mensagens',  render: pagComunicacao },
  { id: 'notificacoes', nome: 'Notificações', icone: 'chamado',    render: pagNotificacoes },
  { id: 'funcionarios', nome: 'Funcionários', icone: 'usuarios',   render: pagFuncionarios },
  { id: 'usuarios',     nome: 'Usuários',     icone: 'seguranca',  render: pagUsuarios }
];

const VERSAO = '0.1.0';

// ─── Boot ─────────────────────────────────────────────────────────────
async function boot({ comSplash = false } = {}) {
  iniciarTema();     // antes de pintar: trocar de tema com a tela pronta pisca
  montarSprite();
  if (!document.getElementById('css-icones')) {
    const st = document.createElement('style');
    st.id = 'css-icones';
    st.textContent = ICON_CSS;
    document.head.appendChild(st);
  }

  // O splash roda UMA vez por sessão do navegador. Ver a marca ao abrir o
  // sistema é bom; vê-la a cada clique de "sair" seria irritante.
  if (comSplash && !sessionStorage.getItem('splash_visto')) {
    sessionStorage.setItem('splash_visto', '1');
    await splash();
  }

  const { slug } = slugDoHost();
  estado.tenant = await carregarBranding(slug);
  aplicarTema(estado.tenant);

  const { data: { session } } = await sb.auth.getSession();
  if (!session) return telaLogin();

  estado.perfil = await meuPerfil();
  if (!estado.perfil)          return telaLogin('Perfil não encontrado.');
  if (!estado.perfil.aprovado) return telaPendente();
  if (estado.perfil.senha_alterada === false) return telaTrocarSenha();

  await carregarPermissoes();
  telaPrincipal();
}

/** Splash da marca — 3 segundos. */
function splash() {
  return new Promise((resolve) => {
    app.innerHTML = `
      <div class="splash">
        <img src="/logo-splash.png" alt="Smart Gestão Imobiliária">
        <div class="nome">Smart Gestão Imobiliária</div>
        <div class="barra"><i></i></div>
      </div>`;
    setTimeout(resolve, 3000);
  });
}

async function carregarPermissoes() {
  const { data: t } = await sb
    .from('tenants')
    .select('modulos,exige_2fa_admin,dpo_nome,dpo_email')
    .eq('id', estado.perfil.tenant_id)
    .maybeSingle();
  estado.modulos = (t && t.modulos) || {};
  estado.dpo = t || {};

  const perms = {};
  await Promise.all(ABAS.map(async (a) => {
    const { data } = await sb.rpc('pode_ver', { p_aba: a.id });
    perms[a.id] = data === true;
  }));
  perms.inicio = true;
  estado.permissoes = perms;
}

// ─── Telas de autenticação ────────────────────────────────────────────
function moldura(conteudo) {
  return `
    <div class="auth-tela">
      <div class="topo-direito">${botaoTemaHtml('icone')}</div>
      <div class="auth">
        <div class="marca">
          <img src="/logo-topo.png" alt="">
          <h1>${escapeHtml(estado.tenant?.nome || 'Smart Gestão Imobiliária')}</h1>
          <p class="sub">Smart Gestão Imobiliária</p>
        </div>
        ${conteudo}
      </div>
      ${rodapeHtml({ compacto: true })}
    </div>`;
}

// =====================================================================
// LOGIN — e o segundo fator que agora vale alguma coisa.
//
// O QUE ESTAVA ERRADO (e o Monteiro de Moraes já fazia certo):
//
//   O front chamava signInWithPassword() no Supabase e SÓ ENTÃO mostrava a
//   tela do código. Ou seja: quem tivesse a senha já saía dali com a sessão
//   válida na mão — bastava fechar a telinha do 2FA. O segundo fator era um
//   enfeite, não uma trava.
//
//   AGORA: o browser NUNCA autentica direto. Quem faz isso é a function
//   auth-login, no servidor. Se o 2FA for exigido, ela devolve um TICKET
//   cifrado (a sessão viaja dentro dele, opaca, por 5 minutos) — e o ticket
//   só vira sessão em auth-2fa, depois do código conferido.
//
//   Nenhuma sessão chega ao navegador sem passar pelo segundo fator.
// =====================================================================

/** Injeta no cliente a sessão que veio do servidor. */
async function entrarCom(sessao) {
  const { error } = await sb.auth.setSession({
    access_token: sessao.access_token,
    refresh_token: sessao.refresh_token
  });
  if (error) return telaLogin('Não consegui abrir a sessão. Tente de novo.');
  boot();
}

const chamar = (fn, body) =>
  fetch(`/.netlify/functions/${fn}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  }).then(x => x.json()).catch(() => ({ error: 'Falha de conexão' }));

function telaLogin(erro = '') {
  app.innerHTML = moldura(`
    ${erro ? `<div class="aviso erro">${icon('erro')} ${escapeHtml(erro)}</div>` : ''}
    <form id="f">
      <label>E-mail<input name="email" type="email" required autocomplete="username"></label>
      <label>Senha<input name="senha" type="password" required autocomplete="current-password"></label>
      <button class="primaria" type="submit">${icon('chave')} Entrar</button>
    </form>
    <button class="link" id="esqueci" style="margin-top:12px;width:100%">Esqueci minha senha</button>
  `);
  ligarRodape();

  document.getElementById('f').onsubmit = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const btn = e.target.querySelector('button[type=submit]');
    btn.disabled = true;
    btn.textContent = 'Entrando…';

    // O rate-limit vive na function (5 falhas/usuário, 20/IP em 15 min): o
    // Supabase sozinho não bloqueia força bruta concentrada num só usuário.
    const r = await chamar('auth-login', { email: fd.get('email'), senha: fd.get('senha') });

    if (r.error) return telaLogin(r.error);

    // Master com 2FA: o código decide. Master sem 2FA: cadastra AGORA.
    if (r.mfa_required)        return telaCodigo2FA(r.mfa_ticket);
    if (r.mfa_enroll_required) return telaCadastrar2FA(r.mfa_ticket);

    if (r.sessao) return entrarCom(r.sessao);
    telaLogin('Resposta inesperada do servidor.');
  };

  document.getElementById('esqueci').onclick = async () => {
    const email = prompt('Seu e-mail:');
    if (!email) return;
    await sb.auth.resetPasswordForEmail(email, { redirectTo: location.origin });
    alert('Se o e-mail estiver cadastrado, enviamos o link de redefinição.');
  };
}

// ─── Tela do código (app autenticador OU código de backup) ────────────
function telaCodigo2FA(ticket, erro = '') {
  app.innerHTML = moldura(`
    <p class="sub">${icon('seguranca')} Código do seu app autenticador</p>
    ${erro ? `<div class="aviso erro">${escapeHtml(erro)}</div>` : ''}
    <form id="f">
      <label>Código
        <input name="codigo" maxlength="9" required autocomplete="one-time-code"
               inputmode="numeric" placeholder="000000"
               style="font-size:21px;letter-spacing:6px;text-align:center;text-transform:uppercase">
      </label>
      <button class="primaria" type="submit">Verificar</button>
    </form>
    <p class="dica" style="margin-top:14px;text-align:center">
      Perdeu o celular? Use um dos <b>códigos de backup</b> que você imprimiu
      (formato ABCD-2345) — cada um serve uma vez só.
    </p>`);
  ligarRodape();

  document.getElementById('f').onsubmit = async (e) => {
    e.preventDefault();
    const btn = e.target.querySelector('button');
    btn.disabled = true;
    btn.textContent = 'Verificando…';

    const codigo = String(new FormData(e.target).get('codigo') || '').trim();
    const r = await chamar('auth-2fa', { mfa_ticket: ticket, codigo });

    if (r.expirado)     return telaLogin('A sessão expirou. Entre de novo.');
    if (r.recadastrar)  return telaLogin(r.error);
    if (!r.ok)          return telaCodigo2FA(ticket, r.error || 'Código inválido');

    if (r.usou_backup && r.aviso) alert(r.aviso);
    entrarCom(r.sessao);
  };
}

// ─── Cadastro obrigatório do 2FA, no login ────────────────────────────
//
// Master sem 2FA não entra. Deixar entrar "para configurar depois" é deixar
// a porta destrancada com um bilhete pedindo para trancarem mais tarde.
async function telaCadastrar2FA(ticket, erro = '') {
  app.innerHTML = moldura(`
    <p class="sub">${icon('seguranca')} Ative a verificação em duas etapas</p>
    <div class="aviso info">${icon('info')} Você é master: edita contrato, cobrança e dado
      pessoal de terceiros. O sistema não libera o acesso sem o segundo fator.</div>
    <div id="area"><div class="carregando">Gerando o QR Code…</div></div>`);
  ligarRodape();

  const area = document.getElementById('area');
  const r = await chamar('mfa-setup', { mfa_ticket: ticket, acao: 'iniciar' });

  if (r.expirado) return telaLogin('A sessão expirou. Entre de novo.');
  if (r.error)    return telaLogin(r.error);

  area.innerHTML = `
    ${erro ? `<div class="aviso erro">${escapeHtml(erro)}</div>` : ''}
    <div class="qr-2fa" style="flex-direction:column;align-items:center">
      <img src="${r.qr}" alt="QR Code" width="200" height="200">
      <p class="dica" style="text-align:center">Leia com o Google Authenticator.<br>
         Não consegue ler? Digite: <code>${escapeHtml(r.segredo)}</code></p>
    </div>
    <form id="f2">
      <label>Código de 6 dígitos do app
        <input name="codigo" inputmode="numeric" maxlength="6" required
               style="font-size:21px;letter-spacing:6px;text-align:center">
      </label>
      <button class="primaria" type="submit">Ativar e entrar</button>
    </form>`;

  document.getElementById('f2').onsubmit = async (e) => {
    e.preventDefault();
    const btn = e.target.querySelector('button');
    btn.disabled = true;
    btn.textContent = 'Verificando…';

    const codigo = new FormData(e.target).get('codigo');
    const c = await chamar('mfa-setup', { mfa_ticket: ticket, acao: 'confirmar', codigo });

    if (c.expirado) return telaLogin('A sessão expirou. Entre de novo.');
    if (!c.ok)      return telaCadastrar2FA(ticket, c.error || 'Código incorreto');

    // Os códigos de backup aparecem AQUI e nunca mais. Só entramos depois
    // que a pessoa confirmar que os guardou.
    telaCodigosBackup(c.backup_codes, () => entrarCom(c.sessao));
  };
}

// ─── Os 8 códigos: a única vez que eles existem ───────────────────────
export function telaCodigosBackup(codigos, aoConfirmar) {
  app.innerHTML = moldura(`
    <p class="sub">${icon('chave')} Seus códigos de recuperação</p>
    <div class="aviso atencao">${icon('alerta')}
      <div><b>Guarde agora. Eles não aparecem de novo.</b>
      Se o celular sumir, é com eles que você entra. Cada um serve uma vez.</div>
    </div>

    <div class="codigos-backup" id="lista">
      ${codigos.map(c => `<code>${escapeHtml(c)}</code>`).join('')}
    </div>

    <div style="display:flex;gap:8px;margin-top:14px">
      <button id="imprimir" style="flex:1">${icon('pdf')} Imprimir</button>
      <button id="copiar" style="flex:1">${icon('documentos')} Copiar</button>
    </div>

    <label style="margin-top:16px;display:flex;gap:9px;align-items:flex-start">
      <input type="checkbox" id="guardei" style="width:auto;margin-top:3px">
      <span>Guardei os códigos num lugar seguro (impressos ou no gerenciador de senhas).</span>
    </label>
    <button class="primaria" id="ok" disabled style="width:100%;margin-top:10px">Continuar</button>`);
  ligarRodape();

  document.getElementById('imprimir').onclick = () => {
    const j = window.open('', '_blank');
    j.document.write(`
      <html><head><title>Códigos de recuperação — Smart Gestão Imobiliária</title>
      <style>
        body{font:15px/1.7 -apple-system,sans-serif;padding:40px;max-width:640px}
        h1{font-size:19px} code{display:inline-block;font-size:19px;letter-spacing:2px;
        border:1px solid #ccc;border-radius:6px;padding:9px 14px;margin:5px}
        .aviso{color:#92400E;background:#FEF3C7;padding:12px;border-radius:8px}
      </style></head><body>
      <h1>Códigos de recuperação — Smart Gestão Imobiliária</h1>
      <p>Conta: <b>${escapeHtml(estado.perfil?.email || '')}</b> ·
         Gerados em ${new Date().toLocaleDateString('pt-BR')}</p>
      <p class="aviso">Cada código serve <b>uma única vez</b>. Guarde este papel
         em local seguro — quem tiver estes códigos e a sua senha entra na sua conta.</p>
      <p>${codigos.map(c => `<code>${c}</code>`).join(' ')}</p>
      </body></html>`);
    j.document.close();
    j.print();
  };

  document.getElementById('copiar').onclick = async () => {
    await navigator.clipboard.writeText(codigos.join('\n'));
    document.getElementById('copiar').textContent = 'Copiado';
  };

  const chk = document.getElementById('guardei');
  const ok = document.getElementById('ok');
  chk.onchange = () => { ok.disabled = !chk.checked; };
  ok.onclick = aoConfirmar;
}

function telaTrocarSenha(erro = '') {
  app.innerHTML = moldura(`
    <p class="sub">Primeiro acesso — defina sua senha</p>
    ${erro ? `<div class="aviso erro">${escapeHtml(erro)}</div>` : ''}
    <form id="f">
      <label>Nova senha<input name="s1" type="password" required autocomplete="new-password"></label>
      <label>Repita<input name="s2" type="password" required autocomplete="new-password"></label>
      <small class="dica">Mínimo 10 caracteres, com maiúscula, minúscula, número e símbolo.</small>
      <button class="primaria" type="submit">Salvar</button>
    </form>`);
  ligarRodape();

  document.getElementById('f').onsubmit = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const s = String(fd.get('s1'));
    if (s !== String(fd.get('s2'))) return telaTrocarSenha('As senhas não conferem');
    const forte = s.length >= 10 && /[a-z]/.test(s) && /[A-Z]/.test(s) && /\d/.test(s) && /[^A-Za-z0-9]/.test(s);
    if (!forte) return telaTrocarSenha('Senha fraca: 10+ caracteres, com maiúscula, minúscula, número e símbolo');

    const { error } = await sb.auth.updateUser({ password: s });
    if (error) return telaTrocarSenha(error.message);
    await sb.from('perfis').update({ senha_alterada: true }).eq('id', estado.perfil.id);
    boot();
  };
}

function telaPendente() {
  app.innerHTML = moldura(`
    <div class="aviso atencao">${icon('alerta')} Seu cadastro aguarda liberação de um administrador.</div>
    <button class="link" id="sair" style="width:100%">Sair</button>`);
  ligarRodape();
  document.getElementById('sair').onclick = async () => { await sb.auth.signOut(); boot(); };
}

// ─── App ──────────────────────────────────────────────────────────────
function telaPrincipal() {
  const visiveis = ABAS.filter(a => estado.permissoes[a.id]);
  const inicial = (estado.perfil.nome || '?').trim()[0].toUpperCase();

  app.innerHTML = `
    <div class="app">
      <!-- Barra superior: só existe no celular. No desktop, a marca já está
           na lateral, e repetir seria roubar altura de tela de quem tem
           mouse e não precisa de alvo grande. -->
      <header class="barra-mobile">
        <button class="icone" id="abrir-menu" aria-label="Menu">${icon('quadro')}</button>
        <b id="titulo-mobile">Início</b>
        ${dataDeHojeHtml()}
        ${botaoTemaHtml('icone')}
      </header>

      <div class="cortina" id="cortina"></div>

      <aside class="lateral">
        <div class="marca">
          <img src="/logo-topo.png" alt="">
          <div class="txt">
            <b>${escapeHtml(estado.tenant?.nome || 'Cliente')}</b>
            <span>SMART GESTÃO IMOBILIÁRIA</span>
          </div>
        </div>

        <nav class="abas">
          ${visiveis.map(a => `<button data-aba="${a.id}">${icon(a.icone)} ${a.nome}</button>`).join('')}
        </nav>

        <!-- Os selos moram ao pé das abas, pequenos: são garantia, não
             propaganda. Quem precisa deles procura; quem não precisa não
             deve tropeçar neles o dia inteiro. -->
        ${selosHtml()}

        <div class="usuario-box">
          <div class="avatar" id="meu-avatar">${escapeHtml(inicial)}</div>
          <div class="quem">
            <b>${escapeHtml(estado.perfil.nome)}</b>
            <span>${escapeHtml(estado.perfil.papel)}</span>
          </div>
          <button class="icone" id="minha-conta" title="Minha conta (foto, telefone, 2FA)">${icon('config')}</button>
          <button class="icone" id="sair" title="Sair">${icon('sair')}</button>
        </div>
      </aside>

      <div class="conteudo">
        <div class="topo-direito">
          ${dataDeHojeHtml()}
          ${botaoTemaHtml('icone')}
        </div>
        <main id="conteudo"></main>
        ${rodapeHtml({})}
      </div>
    </div>`;

  document.getElementById('sair').onclick = async () => {
    await sb.auth.signOut();
    boot();
  };
  document.getElementById('minha-conta').onclick = () =>
    minhaConta(estado.perfil, () => boot());

  document.querySelectorAll('.abas button').forEach(b => {
    b.onclick = () => { abrir(b.dataset.aba); fecharMenu(); };
  });
  ligarRodape();

  // ── Celular ───────────────────────────────────────────────────────
  const appEl = document.querySelector('.app');
  document.getElementById('abrir-menu').onclick = () => appEl.classList.toggle('menu-aberto');
  document.getElementById('cortina').onclick = fecharMenu;

  // Deslizar troca de aba. É o gesto que o polegar já faz sozinho — e no
  // celular vale mais que qualquer menu, porque não exige mirar em nada.
  if (ehTouch()) {
    ligarSwipe(document.querySelector('.conteudo'), {
      aoEsquerda: () => passarAba(+1),
      aoDireita:  () => passarAba(-1)
    });
  }

  if (estado.perfil.foto_url) mostrarFoto(document.getElementById('meu-avatar'), estado.perfil.foto_url);

  abrir(location.hash.slice(1) || 'inicio');
}

const fecharMenu = () => document.querySelector('.app')?.classList.remove('menu-aberto');

/** Próxima/anterior entre as abas que o usuário PODE ver. */
function passarAba(passo) {
  const visiveis = ABAS.filter(a => estado.permissoes[a.id]);
  const i = visiveis.findIndex(a => a.id === (location.hash.slice(1) || 'inicio'));
  const alvo = visiveis[i + passo];
  if (alvo) abrir(alvo.id);   // nas pontas não circula: dar a volta desorienta
}

/**
 * Data de hoje, por extenso, na faixa do topo.
 *
 * Fica no shell, e não na tela de Início, porque a data vale para o app
 * inteiro: quem está lançando um pagamento ou fechando um repasse também
 * precisa saber que dia é hoje. Repetir por tela seria repetir o mesmo dado
 * — e era isso que espremia o cabeçalho do Início.
 *
 * Em tela estreita, o dia da semana some antes da data (é o que menos
 * importa). Encolher tudo até truncar seria o pior dos dois mundos.
 */
function dataDeHojeHtml() {
  const hoje = new Date();
  const semana = hoje.toLocaleDateString('pt-BR', { weekday: 'long' });
  const dia = hoje.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
  return `
    <span class="data-hoje" title="${escapeHtml(semana + ', ' + dia)}">
      ${icon('calendario')}
      <span class="semana">${escapeHtml(semana)},</span>
      <span>${escapeHtml(dia)}</span>
    </span>`;
}

// Selos: pequenos, no pé da barra lateral. "Dados isolados" saiu — dizer
// que o dado está isolado é descrever a arquitetura para quem não pediu.
// Site seguro e LGPD ficam porque o cliente do cliente pergunta por eles.
function selosHtml() {
  const seguro = location.protocol === 'https:' || location.hostname === 'localhost';
  return `
    <div class="selos-lateral">
      <span class="selo mini ${seguro ? 'ok' : 'atencao'}" title="${seguro
        ? 'Conexão criptografada (HTTPS)' : 'Certificado em emissão'}">
        ${icon('cadeado')} ${seguro ? 'Site seguro' : 'Sem HTTPS'}
      </span>
      <button class="selo mini ok" id="rodape-lgpd" title="Lei 13.709/2018 — ver a política">
        ${icon('seguranca')} LGPD
      </button>
    </div>`;
}

async function abrir(id) {
  const aba = ABAS.find(a => a.id === id);
  if (!aba || !estado.permissoes[id]) return abrir('inicio');

  location.hash = id;
  document.querySelectorAll('.abas button').forEach(b =>
    b.classList.toggle('ativa', b.dataset.aba === id));

  const t = document.getElementById('titulo-mobile');
  if (t) t.textContent = aba.nome;

  const el = document.getElementById('conteudo');
  el.innerHTML = '<p class="carregando">Carregando…</p>';
  el.scrollIntoView({ block: 'start' });   // trocou de aba no celular: começa do topo
  try {
    await aba.render(el, estado);
    ligarZoomFotos(el);    // toda foto da tela abre com pinça, sem a tela pedir
  } catch (e) {
    el.innerHTML = `<div class="aviso erro">${icon('erro')} ${escapeHtml(e.message)}</div>`;
    console.error(e);
  }
}

// =====================================================================
// Rodapé — selos, dados do cliente e link para a política.
//
// Escolha de palavra que importa: "Em conformidade com a LGPD", não
// "Certificado LGPD". Certificação sugere que um terceiro auditou e
// atestou — e ninguém atestou. Conformidade é o que a empresa declara e
// sustenta com o que faz: consentimento registrado, dado isolado por
// cliente, descarte no prazo, encarregado nomeado. Tudo isso o sistema
// realmente entrega, então a frase é verdadeira e defensável.
// =====================================================================
function rodapeHtml({ compacto = false } = {}) {
  const seguro = location.protocol === 'https:' || location.hostname === 'localhost';
  const dpo = estado.dpo || {};
  const ano = new Date().getFullYear();

  // Na tela de login não há barra lateral, então os selos aparecem aqui —
  // pequenos, e é o único lugar em que eles ainda vivem no rodapé.
  return `
    <footer class="rodape ${compacto ? 'compacto' : ''}">
      ${compacto ? `
      <div class="selos">
        <span class="selo mini ${seguro ? 'ok' : 'atencao'}">
          ${icon('cadeado')} ${seguro ? 'Site seguro' : 'Sem HTTPS'}
        </span>
        <button class="selo mini ok" id="rodape-lgpd">${icon('seguranca')} LGPD</button>
      </div>` : `
      <div class="legal">
        ${estado.tenant?.nome ? `<strong>${escapeHtml(estado.tenant.nome)}</strong> · ` : ''}
        Encarregado de Dados (DPO): ${escapeHtml(dpo.dpo_nome || 'não informado')}
        ${dpo.dpo_email ? ` · <a href="mailto:${escapeHtml(dpo.dpo_email)}">${escapeHtml(dpo.dpo_email)}</a>` : ''}
        · <a href="#" id="rodape-politica">Política de Privacidade</a>
      </div>`}
      <span class="assinatura">© ${ano} Smart Gestão Imobiliária · v${VERSAO}</span>
    </footer>`;
}

function ligarRodape() {
  const b = document.getElementById('rodape-lgpd');
  if (b) b.onclick = mostrarPolitica;
  const l = document.getElementById('rodape-politica');
  if (l) l.onclick = (e) => { e.preventDefault(); mostrarPolitica(); };

  ligarTema();      // o botão de tema vive no rodapé, que está em toda tela
  ativarOlhos();    // e todo campo de senha ganha o olho, sem cada tela pedir
}

async function mostrarPolitica() {
  const { slug } = slugDoHost();
  const { data: t } = await sb.from('tenants').select('id').eq('slug', slug).maybeSingle();

  let pol = null;
  if (t) {
    const { data } = await sb
      .from('politicas')
      .select('titulo,corpo,versao,publicada_em')
      .eq('tipo', 'privacidade')
      .eq('vigente', true)
      .maybeSingle();
    pol = data;
  }

  const fundo = document.createElement('div');
  fundo.className = 'modal-fundo';
  fundo.innerHTML = `
    <div class="modal">
      <header>
        ${icon('documentos')}
        <h3>${escapeHtml(pol?.titulo || 'Política de Privacidade')}</h3>
        <button class="icone" data-fechar>${icon('erro')}</button>
      </header>
      <div class="corpo">
        ${pol
          ? `<span class="badge cinza">versão ${escapeHtml(pol.versao)}</span>
             <div class="texto-legal">${escapeHtml(pol.corpo)}</div>`
          : `<div class="aviso atencao">${icon('alerta')} Nenhuma política publicada ainda.
             Sem ela, o cadastro público de candidatos fica bloqueado — um consentimento que não
             aponta para um texto não prova nada.</div>`}
      </div>
      <footer><button data-fechar>Fechar</button></footer>
    </div>`;
  fundo.querySelectorAll('[data-fechar]').forEach(b => (b.onclick = () => fundo.remove()));
  fundo.onclick = (e) => { if (e.target === fundo) fundo.remove(); };
  document.body.appendChild(fundo);
}

export { abrir };
boot({ comSplash: true });
